/**
 * The one releases read both pages share.
 *
 * portal-api returns ONE flat list — releases and the versions inside them
 * are all `releases` rows — and the folders page and the release page both
 * need it nested (`groupIntoFamilies`). Reading it through one hook with one
 * query key means going from the folders into a release paints from cache,
 * and a write on either page invalidates the other.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

import { groupIntoFamilies, sortReleasesNewestFirst, type ReleaseFamily } from "./release-families.js";
import { listReleases, type ReleaseDTO } from "./releases-api.js";

export const RELEASES_QUERY_KEY = ["admin", "releases"] as const;

export interface ReleasesRead {
  releases: ReleaseDTO[];
  families: ReleaseFamily[];
  /** The newest released VERSION — a release is a folder name, not something
   *  anyone runs. */
  latest: ReleaseDTO | undefined;
  versionCount: number;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => Promise<unknown>;
  invalidate: () => Promise<void>;
}

export function useReleases(): ReleasesRead {
  const qc = useQueryClient();
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: RELEASES_QUERY_KEY,
    queryFn: () => listReleases(),
  });

  const releases = useMemo(() => sortReleasesNewestFirst(data ?? []), [data]);
  const families = useMemo(() => groupIntoFamilies(data ?? []), [data]);
  const latest = useMemo(() => {
    const versions = releases.filter((r) => r.parent_release_id !== null);
    return versions.find((r) => r.released) ?? versions[0] ?? releases[0];
  }, [releases]);
  const versionCount = releases.filter((r) => r.parent_release_id !== null).length;

  return {
    releases,
    families,
    latest,
    versionCount,
    isLoading,
    isError,
    error,
    refetch,
    invalidate: () => qc.invalidateQueries({ queryKey: RELEASES_QUERY_KEY }),
  };
}
